import React, { HTMLAttributes, useState } from "react";
import { animated, useSpring } from "react-spring";
import BaseButton from "../../atoms/baseButton";
import SectionTitle from "../../atoms/sectionTitle";

const MACHINE_INFO = [
  {
    title: "機身尺寸",
    englishTitle: "Machine Size",
    content: "W1040mm x D580mm x H2200mm",
  },
  {
    title: "機身重量",
    englishTitle: "Machine Weight",
    content: "<500kg",
  },
  {
    title: "外殼材質",
    englishTitle: "Shell Material",
    content: "金屬(Metal)",
  },
  {
    title: "防水等級",
    englishTitle: "Waterproof Level",
    content: "IP54",
  },
  {
    title: "回定方式",
    englishTitle: "Return Method",
    content: "地面(Ground)",
  },
] as const;

const PORT_INFO = [
  {
    title: "供電方式",
    englishTitle: "Power Supply Method",
    content: "直流電(DC)",
  },
  {
    title: "制式",
    englishTitle: "Standard",
    content: "CCS2",
  },
  {
    title: "供電功率",
    englishTitle: "Power Supply Capacity",
    content: "高達(Up to) 120kW",
  },
  {
    title: "電纜長度",
    englishTitle: "Cable Length",
    content: "5米(Meters)",
  },
] as const;

interface IChargerSpecSection {}

type TChargerSpecSectionProps = IChargerSpecSection &
  HTMLAttributes<HTMLDivElement>;

const ChargerSpecSection: React.FC<TChargerSpecSectionProps> = (props) => {
  const [showMachine, setShowMachine] = useState(true);

  const machineStyle = useSpring({
    opacity: showMachine ? 1 : 0, // Fade in when `showMachine` is true
    transform: showMachine ? "translateX(0%)" : "translateX(-20%)", // Slight movement for effect
    config: { tension: 200, friction: 20 }, // Smooth animation
  });

  const portStyle = useSpring({
    opacity: showMachine ? 0 : 1, // Fade in when `showMachine` is false
    transform: showMachine ? "translateX(20%)" : "translateX(0%)", // Slight movement for effect
    config: { tension: 200, friction: 20 }, // Smooth animation
  });

  return (
    <div className="justify-center flex flex-col p-8 laptop:px-[10%]">
      <SectionTitle chineseTitle="充電站規格" englishTitle="Spec" />

      <div className="flex flex-col laptop:flex-row justify-center items-center gap-10">
        {/* Left button section */}
        <div className="flex flex-row laptop:flex-col gap-6 z-10 text-white">
          <BaseButton
            colorBase={showMachine ? "blue" : "gray"}
            onClick={() => setShowMachine(true)}
          >
            <div className="flex flex-col">
              <span className="text-sm">機身</span>
              <span className="text-xs">Machine</span>
            </div>
          </BaseButton>
          <BaseButton
            colorBase={showMachine ? "gray" : "blue"}
            onClick={() => setShowMachine(false)}
          >
            <div className="flex flex-col">
              <span className="text-sm">接口</span>
              <span className="text-xs">Port</span>
            </div>
          </BaseButton>
        </div>

        {/* Spec info section */}
        <div className="relative flex justify-center items-center min-h-[400px] overflow-hidden">
          {/* Machine Spec */}
          <animated.div
            className={
              "h-full flex flex-col laptop:flex-row gap-10 items-center"
            }
            style={machineStyle}
          >
            <img
              src={require("../../../assets/images/machine_cover.png")}
              alt="Machine Spec"
              className="object-contain w-[200px] h-[200px] laptop:w-[350px] laptop:h-[325px]"
            />
            <div className="flex flex-col gap-2 laptop:gap-3 text-white justify-center items-center laptop:text-xl">
              {MACHINE_INFO.map((info) => {
                return (
                  <div
                    className="flex flex-row w-full items-center gap-5"
                    key={info.englishTitle}
                  >
                    <span className="flex flex-col flex-1 gap-1">
                      <span className="">{info.title}</span>
                      <span className="text-sm">{info.englishTitle}</span>
                    </span>
                    <span className="flex flex-1 text-base">
                      {info.content}
                    </span>
                  </div>
                );
              })}
            </div>
          </animated.div>

          {/* Port Spec */}
          <animated.div
            className={
              "flex absolute top-0 h-full flex-col laptop:flex-row gap-10 items-center"
            }
            style={portStyle}
          >
            <img
              src={require("../../../assets/images/port_cover.png")}
              alt="Port Spec"
              className="object-contain w-[200px] h-[200px] laptop:w-[350px] laptop:h-[325px]"
            />
            <div className="flex flex-col gap-3 text-white justify-center items-center laptop:text-xl">
              {PORT_INFO.map((info) => {
                return (
                  <div
                    className="flex flex-row w-full items-center gap-5"
                    key={info.englishTitle}
                  >
                    <span className="flex flex-col flex-1 gap-1">
                      <span className="">{info.title}</span>
                      <span className="text-sm">{info.englishTitle}</span>
                    </span>
                    <span className="flex flex-1 text-base">
                      {info.content}
                    </span>
                  </div>
                );
              })}
            </div>
          </animated.div>
        </div>
      </div>
    </div>
  );
};

export default ChargerSpecSection;
